import { Box, Stack, TABULAR, Typography, useTheme } from '@platform/design-system';
import { dollars, formatNumber, resetsAtLocal, spendState } from './format.ts';

/**
 * "$spent of $limit" with a full-width bar, the state in words and the count of calls (design
 * standard DS-COST-01/02): the primary token below 80%, warning from 80%, error at the limit.
 * The state is always written, never colour alone.
 */
export function SpendBar({
  label,
  period,
  spentMicroUsd,
  limitMicroUsd,
  calls,
  resetsEvery,
}: {
  label: string;
  /** How the period is named in the sentence: "this month's", "today's". */
  period: string;
  spentMicroUsd: number;
  limitMicroUsd: number | undefined;
  calls: number;
  /** The limit's UTC period: the bar then says when it resets, in the viewer's time. */
  resetsEvery?: 'day' | 'month';
}) {
  const theme = useTheme();
  const state = spendState(spentMicroUsd, limitMicroUsd);
  const percent =
    limitMicroUsd === undefined
      ? 0
      : limitMicroUsd === 0
        ? 100
        : Math.floor((spentMicroUsd * 100) / limitMicroUsd);
  const fill =
    state === 'stopped'
      ? theme.palette.error.main
      : state === 'warning'
        ? theme.palette.warning.main
        : theme.palette.primary.main;
  const words =
    state === 'unlimited'
      ? `No ${period} limit`
      : state === 'stopped'
        ? `${period[0]!.toUpperCase()}${period.slice(1)} limit reached · calls are refused`
        : `${percent}% of ${period} limit`;
  const callWords = `${formatNumber(calls)} ${calls === 1 ? 'call' : 'calls'}`;
  return (
    <Stack spacing={0.5} sx={{ minWidth: 160 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ ...TABULAR, fontWeight: 500 }}>
          {dollars(spentMicroUsd)}
          {limitMicroUsd !== undefined ? ` of ${dollars(limitMicroUsd)}` : ''}
        </Typography>
      </Stack>
      <Box
        role="meter"
        aria-label={`${label}: ${words}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(percent, 100)}
        aria-valuetext={words}
        sx={{
          height: 6,
          width: '100%',
          borderRadius: 3,
          bgcolor: 'action.hover',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${Math.min(percent, 100)}%`,
            bgcolor: fill,
            borderRadius: 3,
          }}
        />
      </Box>
      <Typography
        variant="caption"
        color={state === 'stopped' || state === 'warning' ? 'text.primary' : 'text.secondary'}
        sx={{ ...TABULAR, fontWeight: state === 'stopped' || state === 'warning' ? 500 : 400 }}
      >
        {words} · {callWords}
      </Typography>
      {resetsEvery && limitMicroUsd !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {resetsAtLocal(resetsEvery)}
        </Typography>
      )}
    </Stack>
  );
}
